import React from 'react';
import styled from 'styled-components';

import useTranslation from 'src/hooks/use-translation';
import { GoogleLogin, AppleLogin } from 'src/components/social-login';
import { Divider } from 'src/components/divider';
import useStores from 'shared/hooks/use-stores';
import { PaySignUpButton } from 'src/payments/components/pay-sign-up-button/pay-sign-up-button';
import { useCashBackSurfaceGate } from 'src/payments/hooks/use-cash-back-surface-gate';
import { useDutchiePay } from 'src/payments/hooks/use-dutchie-pay/use-dutchie-pay';
import { usePointsBoostCheckoutTreatment } from 'src/payments/hooks/use-points-boost-checkout-treatment';

import {
  CheckoutSection,
  CheckoutLabel,
  CheckoutLabelGrouping,
  CheckoutBody,
  CheckoutSectionHeading,
} from 'src/checkout/components/styles/common';

export function InlineSocialLogin(): JSX.Element {
  const { t } = useTranslation();
  const { Cart, UI } = useStores();
  const { allowDutchiePayEnrollment } = useDutchiePay();
  const { showCashBack, cashBackValue } = useCashBackSurfaceGate('checkout');
  // Cash-first is built in on both sides: the gate suppresses points whenever a cash/discount
  // incentive is eligible, and PaySignUpButton renders at most one badge if both are passed.
  const { showTreatment: showPointsBoost, pointsBoostLabel } = usePointsBoostCheckoutTreatment();
  // The CTA is an incentive treatment: with neither Cash Back nor Points Boost active the
  // Account section keeps its pre-incentive shape (no PBB button at all).
  const showIncentiveCta = allowDutchiePayEnrollment && (showCashBack || showPointsBoost);

  return (
    <>
      <CheckoutSectionContainer noBorder>
        <CheckoutLabelGrouping id='user-account'>
          <CheckoutLabel>
            <CheckoutSectionHeading>{t('user.account', 'Account')}</CheckoutSectionHeading>
          </CheckoutLabel>
          <CheckoutBody>
            <BodyContentContainer>
              {showIncentiveCta && (
                <ExpressButtonContainer>
                  <PaySignUpButton
                    location='customer-section'
                    includeSubtext={false}
                    cname={Cart.dispensary?.cName ?? ''}
                    enrollmentSource={`checkout-page-${String(UI?.variant)}`}
                    cashBackBadgeValue={showCashBack ? cashBackValue : undefined}
                    pointsBoostLabel={pointsBoostLabel ?? undefined}
                  />
                </ExpressButtonContainer>
              )}
              <SocialLogins>
                <GoogleLogin redirectUrl={window.location.href} />
                <AppleLogin redirectUrl={window.location.href} />
              </SocialLogins>
              <Divider text={t('common.or', 'or checkout as a guest')} />
            </BodyContentContainer>
          </CheckoutBody>
        </CheckoutLabelGrouping>
      </CheckoutSectionContainer>
    </>
  );
}

const BodyContentContainer = styled.div`
  display: flex;
  flex-direction: column;
  max-width: 390px;
`;

const SocialLogins = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const ExpressButtonContainer = styled.div`
  margin-bottom: 16px;
`;

const CheckoutSectionContainer = styled(CheckoutSection)`
  margin-bottom: -29px;
  padding-bottom: 0;
`;
